import standardMtxApi from '@/apis/externalReports/standard/standard_mtx';
import type { StandardReportWithPhotos, StandardReportExt } from '@/models/externalReports/standard/standardReport';
import type { CarInfo, SearchConditions } from '@/models/externalReports/standard/standardMap';
import type { StandardCarExt, StandardCarShow } from '@/models/externalReports/standard/standardCar';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';

const maxDateRange = 31;
const oneDayMsec = 1000 * 60 * 60 * 24;

interface ConvDataParams {
  cars: StandardCarShow[];
  currentCarInfoMap: Partial<Record<number, CarInfo>>;
  userSettings: ExternalReportSettings;
}
export type CurrentReportItemInfo = Pick<
  CarInfo,
  'selectedPhotoId' | 'selectedMtxTs' | 'savedImageMap' | 'loadedMtxs'
>;

export function initSearchConds(): SearchConditions {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return {
    hanName: null,
    status: '全て',
    dateFrom: new Date(now.getTime() - oneDayMsec * maxDateRange),
    dateTo: now,
  };
}

export function validSearchDateRange(searchConds: SearchConditions): boolean {
  const from = searchConds.dateFrom.getTime();
  const to = searchConds.dateTo.getTime();
  return Number.isFinite(from) && Number.isFinite(to) &&
    from <= to && (to - from) <= maxDateRange * oneDayMsec;
}

export function createInitialCarsData(recentData: StandardCarShow[]): StandardCarExt[] {
  return recentData.map(car => {
    return {
      ...car,
      isMoving: false,
      isWorking: false,
      shouldShow: true,
      isSelected: false,
      pos: null,
      reportExt: null,
    };
  });
}

export function convData(params: ConvDataParams): StandardCarExt[] {
  return params.cars.map(car => {
    let reportExt: StandardReportExt | null = null;
    const tmpCar = params.currentCarInfoMap[car.id];
    if (car.report) {
      const itemInfo = tmpCar ? {
        selectedPhotoId: tmpCar.selectedPhotoId,
        selectedMtxTs: tmpCar.selectedMtxTs,
        savedImageMap: tmpCar.savedImageMap,
        loadedMtxs: tmpCar.loadedMtxs,
      } : undefined;
      reportExt = convReport(car.report, itemInfo);
    }
    const shouldShow = true;
    const statusChoices = params.userSettings.choices.status;
    const isWorking = car.status === statusChoices.working.key;
    // tsIsNewがfalseの場合は、停止中とみなす
    const isMoving = car.tsIsNew && (isWorking || car.status === statusChoices.running.key);
    const isSelected = tmpCar?.isSelected ?? false;

    // posはLayerManagerで変換して設定する
    return {
      ...car,
      isMoving,
      isWorking,
      shouldShow,
      isSelected,
      pos: null,
      reportExt,
    };
  });
}

export function shouldShowCar(
  car: StandardCarExt,
  decidedSearchConds: SearchConditions,
): boolean {
  // 検索条件に照らして車を表示すべきか判定
  if (decidedSearchConds.hanName &&
    car.hanName !== decidedSearchConds.hanName) {
    return false;
  }
  if (decidedSearchConds.status !== '' && decidedSearchConds.status !== '全て') {
    if (car.status !== decidedSearchConds.status) {
      return false;
    }
  }

  return true;
}

export const convReport = (
  report: StandardReportWithPhotos,
  currentReportItemInfo?: CurrentReportItemInfo,
): StandardReportExt => {
  const contentsDisp = report.otherContent
    ? report.contents.replace('その他', `その他(${report.otherContent})`)
    : report.contents;
  const loadedMtxs = currentReportItemInfo?.loadedMtxs || [];
  const reportExt: StandardReportExt = {
    ...report,
    contentsDisp,
    photosExt: report.photos.map(photo => ({
      ...photo,
      isSelected: currentReportItemInfo?.selectedPhotoId === photo.id,
      hanName: report.hanName ?? '', // Ensure hanName is always a string
      reportContents: contentsDisp,
      reportRoadNames: report.roadNames,
      savedImage: null,
      samePosPhotos: [],
    })),
    mtxsExt: loadedMtxs,
    isSelected: false,
    isMtxsLoaded: loadedMtxs.length > 0,
  };
  for (const photo of reportExt.photosExt) {
    // 取得済みの画像は再取得しない
    photo.savedImage = currentReportItemInfo?.savedImageMap?.[photo.id] ?? null;
    photo.samePosPhotos = reportExt.photosExt.filter(
      (e) => e.lat === photo.lat && e.lon === photo.lon,
    );
  }
  return reportExt;
};

export const loadStandardMtxs = async(reports: StandardReportExt[]): Promise<void> => {
  try {
    const standardMtxs = await standardMtxApi.getMtxsByReportIds({
      report_ids: reports.map(report => report.id),
    });
    reports.forEach(report => {
      if (!standardMtxs[report.id]) return;
      report.mtxsExt = standardMtxs[report.id].map(mtx => {
        return {
          ...mtx,
          isSelected: false,
          hanName: report.hanName,
          reportContents: report.contentsDisp,
          reportRoadNames: report.roadNames,
        };
      });
      report.isMtxsLoaded = true;
    });
  } catch (e) {
    console.error('loadStandardMtxs failed', e);
  }
};

export const deselectReportItems = (report: StandardReportExt): void => {
  report.photosExt.forEach(photo => {
    photo.isSelected = false;
  });
  report.mtxsExt.forEach(mtx => {
    mtx.isSelected = false;
  });
};

export const convSearchParamTsTo = (dateTo: Date): Date => {
  return new Date(dateTo.getTime() + oneDayMsec - 1);
};
